<template>
  <v-menu location="bottom end" :close-on-content-click="false" v-model="open" :width="menuWidth">
    <template #activator="{ props }">
      <v-btn icon size="small" variant="text" v-bind="props">
        <v-badge
          :content="alerts.length"
          :color="badgeColor"
          :model-value="alerts.length > 0"
          offset-x="-2"
          offset-y="-2"
        >
          <v-icon size="22" :color="alerts.length ? badgeColor : 'medium-emphasis'">
            {{ alerts.length ? 'mdi-bell-ring-outline' : 'mdi-bell-outline' }}
          </v-icon>
        </v-badge>
        <v-tooltip activator="parent" location="bottom" content-class="bg-background">
          {{ tooltip }}
        </v-tooltip>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex align-center pa-3 gap-2">
        <v-icon size="18" color="primary">mdi-bell-outline</v-icon>
        <span class="menu-title">Alertes</span>
        <v-chip size="x-small" variant="tonal" :color="badgeColor" v-if="alerts.length">
          {{ alerts.length }}
        </v-chip>
        <v-spacer />
        <v-btn icon size="x-small" variant="text" :loading="loading" @click="refresh">
          <v-icon>mdi-refresh</v-icon>
          <v-tooltip activator="parent" location="bottom" content-class="bg-background">Actualiser</v-tooltip>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0 alerts-body">
        <div class="pa-6 text-center" v-if="loading && !alerts.length">
          <v-progress-circular indeterminate color="primary" size="24" />
        </div>

        <div class="pa-6 text-center text-medium-emphasis" v-else-if="!alerts.length">
          <v-icon size="28" class="mb-2">mdi-check-circle-outline</v-icon>
          <h5>Rien à signaler{{ isAdmin ? ' sur la session' : '' }}.</h5>
        </div>

        <div v-else>
          <template v-for="group in groups" :key="group.key">
            <div v-if="isAdmin" class="group-header d-flex align-center gap-2">
              <span>{{ group.label }}</span>
              <v-chip size="x-small" variant="tonal">{{ group.alerts.length }}</v-chip>
            </div>

            <!-- Une alerte occupe autant de lignes qu'il lui en faut : le détail est ce
                 qui permet d'agir, le tronquer viderait le message de son sens. -->
            <div
              v-for="alert in group.alerts"
              :key="alert.id"
              class="alert-item d-flex"
              role="button"
              @click="openAlert(alert)"
            >
              <v-icon size="18" class="alert-icon" :color="severityColor(alert.severity)">
                {{ severityIcon(alert.severity) }}
              </v-icon>

              <div class="alert-body">
                <div class="alert-title" :class="`text-${severityColor(alert.severity)}`">
                  {{ alert.title }}
                </div>
                <div class="alert-detail">{{ alert.detail }}</div>
              </div>

              <v-icon size="16" class="alert-go">mdi-chevron-right</v-icon>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import { useProfileStore } from '@/stores/profile'
import { useCollectionStore } from '@/stores/collections'
import { useAppStore } from '@/stores/appdata.js'

import Job from '@/models/catalogue/Job'
import Skill from '@/models/catalogue/Skill'
import Offer from '@/models/catalogue/Offer'
import Building from '@/models/catalogue/Building'
import Stuff from '@/models/catalogue/Stuff'
import StuffType from '@/models/catalogue/StuffType'
import Tool from '@/models/catalogue/Tool'
import ToolType from '@/models/catalogue/ToolType'
import HrStatus from '@/models/catalogue/HrStatus'
import ContractStatus from '@/models/catalogue/ContractStatus'
import DepotArea from '@/models/catalogue/DepotArea'
import Depot from '@/models/catalogue/Depot'
import Profile from '@/models/account/Profile'

import buildingTypes from '@/data/buildingTypes.js'

import alertsService from '@/services/alerts/alertsService'
import alertsLoader from '@/services/alerts/alertsLoader'
import alertsBus from '@/services/alerts/alertsBus'
import { SEVERITIES, sortAlerts } from '@/services/alerts/alertsCatalog'

/** Délai de regroupement des mutations avant relecture, en millisecondes. */
const REFRESH_DELAY = 700

/**
 * Centre d'alertes du bandeau.
 *
 * Les alertes ne sont pas stockées : elles sont recalculées à partir des données déjà en
 * base, à l'ouverture de l'application puis à la demande. Un administrateur voit toute la
 * session, un joueur uniquement son équipe.
 *
 * La lecture est ponctuelle et volontairement limitée aux domaines utiles : un bandeau
 * présent sur toutes les pages ne doit pas maintenir des dizaines d'abonnements ouverts.
 */
export default {
  data() {
    return {
      profileStore: useProfileStore(),
      collections: useCollectionStore(),
      appStore: useAppStore(),

      open: false,
      loading: false,
      alerts: [],

      // Référentiels, chargés une fois puis réutilisés à chaque recalcul.
      assets: null,
      depotAreas: [],
      depots: [],
      // Noms des participants, résolus une fois puis conservés.
      profileNames: {},

      unsubscribe: null,
      pendingRefresh: false,
      refreshTimer: null,
    }
  },
  async created() {
    // Toute mutation passe par `callFunction` : s'y abonner suffit à couvrir l'ensemble
    // des actions, présentes comme futures, sans toucher aux écrans.
    this.unsubscribe = alertsBus.subscribe(() => this.scheduleRefresh())
    await this.refresh()
  },
  beforeUnmount() {
    if (typeof this.unsubscribe === 'function') this.unsubscribe()
    clearTimeout(this.refreshTimer)
  },
  watch: {
    // Un changement de période ou d'équipe change ce qui est surveillé.
    scope() {
      this.refresh()
    },
    open(opened) {
      if (opened) this.refresh()
    },
  },
  computed: {
    isAdmin() {
      const role = this.profileStore.profile ? this.profileStore.profile.role : null
      return role === 'Admin' || role === 'SuperAdmin'
    },
    /** Équipes surveillées : toute la session pour un administrateur, la sienne sinon. */
    watchedTeams() {
      if (this.isAdmin) return this.collections.teams || []
      return this.profileStore.team ? [this.profileStore.team] : []
    },
    periodId() {
      return this.profileStore.selectedPeriod ? this.profileStore.selectedPeriod.id : null
    },
    /** Signature du contexte : évite de relire la base quand rien n'a bougé. */
    scope() {
      return `${this.periodId}|${this.watchedTeams.map(team => team.id).join(',')}`
    },
    /** Paramètres généraux : le ratio d'encadrement optimal en dépend. */
    parameters() {
      const appdata = this.appStore.appdata
      return appdata && appdata.parameters ? appdata.parameters.general : null
    },
    /** Types d'alerte désactivés par l'administrateur, pour toute la session. */
    alertSettings() {
      const appdata = this.appStore.appdata
      return appdata && appdata.parameters ? appdata.parameters.alerts : null
    },
    /** Assez large pour une phrase complète, sans déborder d'un écran étroit. */
    menuWidth() {
      return this.$vuetify.display.smAndUp ? 480 : 320
    },
    /** Couleur de la pastille : celle du niveau le plus grave présent. */
    badgeColor() {
      let worst = null
      for (const alert of this.alerts) {
        const severity = SEVERITIES[alert.severity]
        if (!severity) continue
        if (!worst || severity.rank < worst.rank) worst = severity
      }
      return worst ? worst.color : 'info'
    },
    tooltip() {
      if (!this.alerts.length) return 'Aucune alerte'
      const scope = this.isAdmin ? 'sur la session' : 'sur votre entreprise'
      return `${this.alerts.length} alerte${this.alerts.length > 1 ? 's' : ''} ${scope}`
    },
    /** Regroupement par équipe, utile seulement à l'administrateur. */
    groups() {
      if (!this.isAdmin) return [{ key: 'all', label: '', alerts: this.alerts }]

      const byTeam = new Map()
      for (const alert of this.alerts) {
        if (!byTeam.has(alert.teamId)) byTeam.set(alert.teamId, [])
        byTeam.get(alert.teamId).push(alert)
      }

      return Array.from(byTeam.entries())
        .map(([teamId, alerts]) => ({ key: teamId, label: alerts[0].teamName || 'Équipe inconnue', alerts }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
  },
  methods: {
    severityColor(severity) {
      return SEVERITIES[severity] ? SEVERITIES[severity].color : 'info'
    },
    severityIcon(severity) {
      return SEVERITIES[severity] ? SEVERITIES[severity].icon : 'mdi-information-outline'
    },

    /**
     * Recalcul différé.
     *
     * Une action déclenche souvent plusieurs mutations d'affilée, et l'écriture doit
     * d'abord être visible en base : le délai laisse la salve se terminer et n'entraîne
     * qu'une seule relecture.
     */
    scheduleRefresh() {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DELAY)
    },

    /** Référentiels nécessaires à la lecture des alertes, chargés une seule fois. */
    async loadAssets() {
      if (this.assets) return this.assets

      const [jobs, skills, offers, buildings, stuffs, stuffTypes, tools, toolTypes, hrStatus, contractStatus] = await Promise.all([
        Job.getAll(), Skill.getAll(), Offer.getAll(),
        Building.getAll(), Stuff.getAll(), StuffType.getAll(),
        Tool.getAll(), ToolType.getAll(),
        // Le catalogue des statuts, sans les états intégrés au code : ce sont eux, et eux
        // seuls, qui donnent lieu à une alerte.
        HrStatus.getAll(), ContractStatus.getAll(),
      ])

      const table = list => {
        const indexed = {}
        // Un document vide se traduit par `null` : l'écarter évite de faire tomber
        // l'indexation, et donc tout le calcul des alertes, sur une donnée abîmée.
        for (const item of list || []) if (item && item.id) indexed[item.id] = item
        return indexed
      }

      this.assets = {
        jobs, skills, offers, stuffTypes, toolTypes,
        buildingTypes: buildingTypes.map(type => ({ id: type.value, name: type.name })),
        jobsTable: table(jobs),
        buildingsTable: table(buildings),
        stuffsTable: table(stuffs),
        toolsTable: table(tools),
        hrStatusTable: table(hrStatus),
        contractStatusTable: table(contractStatus),
      }
      return this.assets
    },

    /**
     * Zones de dépôt visibles et dépôts déjà remis.
     *
     * Un participant n'a pas accès à l'ensemble de la collection : sa lecture est
     * restreinte à son équipe, comme le fait l'écran des dépôts.
     */
    async loadDepots() {
      const areas = await DepotArea.getAll()
      this.depotAreas = areas.filter(area => area.isVisible && !area.isAdmin)

      if (!this.depotAreas.length) {
        this.depots = []
        return
      }

      if (this.isAdmin) {
        this.depots = await Depot.getAll()
      } else {
        const team = this.profileStore.team
        this.depots = team ? await Depot.getByTeam(team.id) : []
      }
    },

    /**
     * Membres d'une équipe, pour les zones de dépôt personnelles.
     *
     * Un participant n'est comptable que de ses propres dépôts : il ne voit donc que
     * lui-même. Un administrateur suit chaque membre, et a besoin de leur nom.
     */
    async teamMembers(team) {
      const profile = this.profileStore.profile

      if (!this.isAdmin) {
        return profile ? [{ id: profile.id, name: this.displayName(profile), self: true }] : []
      }

      const ids = Array.isArray(team.users) ? team.users : []
      await this.resolveNames(ids)
      return ids.map(id => ({ id, name: this.profileNames[id] || 'Participant inconnu', self: false }))
    },

    displayName(profile) {
      const name = [profile.firstname, profile.lastname].filter(Boolean).join(' ').trim()
      return name || profile.email || 'Participant inconnu'
    },

    /** Résout les noms encore inconnus, une seule fois chacun. */
    async resolveNames(ids) {
      const unknown = ids.filter(id => this.profileNames[id] === undefined)
      if (!unknown.length) return

      await Promise.all(unknown.map(async id => {
        try {
          const profile = await Profile.getById(id)
          this.profileNames[id] = profile ? this.displayName(profile) : 'Participant inconnu'
        } catch (error) {
          this.profileNames[id] = 'Participant inconnu'
        }
      }))
    },

    async refresh() {
      // Une demande arrivée pendant un recalcul n'est pas perdue : elle est rejouée
      // ensuite, sinon la dernière action de la salve serait ignorée.
      if (this.loading) {
        this.pendingRefresh = true
        return
      }
      if (!this.periodId || !this.watchedTeams.length) {
        this.alerts = []
        return
      }

      this.loading = true
      try {
        const assets = await this.loadAssets()
        await this.loadDepots()

        const periods = this.collections.allPeriods.length
          ? this.collections.allPeriods
          : this.collections.periods

        const needsMembers = this.depotAreas.some(area => area.isPrivate)

        const contexts = await Promise.all(this.watchedTeams.map(async team =>
          alertsLoader.loadTeamContext({
            team,
            periods,
            periodId: this.periodId,
            assets,
            parameters: this.parameters,
            alertSettings: this.alertSettings,
            depotAreas: this.depotAreas,
            depots: this.depots,
            members: needsMembers ? await this.teamMembers(team) : [],
          })))

        const found = []
        for (const context of contexts) found.push(...alertsService.detect(context))
        this.alerts = sortAlerts(found)
      } catch (error) {
        // Une alerte manquante ne doit jamais empêcher l'application de fonctionner.
        console.warn('[alertes] lecture impossible :', error)
        this.alerts = []
      } finally {
        this.loading = false
        if (this.pendingRefresh) {
          this.pendingRefresh = false
          this.scheduleRefresh()
        }
      }
    },

    /**
     * Ouvre la page concernée. Pour un administrateur, l'équipe visée est d'abord
     * sélectionnée, sans quoi la page afficherait une autre entreprise.
     */
    openAlert(alert) {
      this.open = false

      if (this.isAdmin && alert.teamId) {
        const team = (this.collections.teams || []).find(item => item.id === alert.teamId)
        if (team) this.profileStore.selectedTeam = team
      }

      if (alert.route && this.$route.path !== alert.route) {
        this.$router.push(alert.route).catch(() => {})
      }
    },
  },
}
</script>

<style scoped>
.menu-title {
  font-size: .85rem;
  font-weight: 700;
}
.alerts-body {
  max-height: 65vh;
  overflow-y: auto;
}
.group-header {
  font-size: .7rem;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  padding: 10px 12px 4px;
  opacity: .6;
}
.alert-item {
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, .06);
}
.alert-item:hover {
  background-color: rgba(255, 255, 255, .04);
}
.alert-icon {
  margin-top: 2px;
  flex: 0 0 auto;
}
.alert-body {
  flex: 1 1 auto;
  min-width: 0;
}
.alert-go {
  align-self: center;
  flex: 0 0 auto;
  opacity: .35;
}
.alert-title {
  font-size: .78rem;
  font-weight: 600;
  line-height: 1.25;
}
/* Le détail est une phrase : il passe à la ligne autant que nécessaire. */
.alert-detail {
  font-size: .74rem;
  line-height: 1.35;
  margin-top: 2px;
  white-space: normal;
  overflow-wrap: anywhere;
  opacity: .78;
}
</style>
